import type { PropostaItem, ComboVariation } from '@/types'
import type { FinanceProposalSnapshotRow } from '@/services/financeService'

type SnapshotMeta = {
  subtotal?: number
  desconto?: number
  totalFinal?: number
  validade?: number
  observacoes?: string
  comboVariations?: ComboVariation[]
}

export type ParsedFinanceProposalSnapshot = {
  items: PropostaItem[]
  meta: SnapshotMeta
  pdfUrl: string | null
  comboVariations: ComboVariation[]
}

function parseDados(raw: unknown): SnapshotMeta {
  if (!raw) return {}
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
    if (!parsed || typeof parsed !== 'object') return {}
    const obj = parsed as unknown as Record<string, unknown>
    return {
      subtotal: typeof obj.subtotal === 'number' ? obj.subtotal : undefined,
      desconto: typeof obj.desconto === 'number' ? obj.desconto : undefined,
      totalFinal: typeof obj.totalFinal === 'number' ? obj.totalFinal : undefined,
      validade: typeof obj.validade === 'number' ? obj.validade : undefined,
      observacoes: typeof obj.observacoes === 'string' ? obj.observacoes : undefined,
      comboVariations: Array.isArray(obj.comboVariations)
        ? (obj.comboVariations as ComboVariation[])
        : undefined,
    }
  } catch {
    return {}
  }
}

function normalizeItem(raw: unknown, index: number): PropostaItem | null {
  if (!raw || typeof raw !== 'object') return null
  const item = raw as unknown as Record<string, unknown>
  const descricao =
    (typeof item.descricao === 'string' && item.descricao) ||
    (typeof item.nome === 'string' && item.nome) ||
    (typeof item.name === 'string' && item.name) ||
    `Item ${index + 1}`
  const quantidade = typeof item.quantidade === 'number' ? item.quantidade : 1
  const valorUnitario =
    typeof item.valorUnitario === 'number'
      ? item.valorUnitario
      : typeof item.valor === 'number'
        ? item.valor / 100
        : 0
  const total =
    typeof item.total === 'number' ? item.total : valorUnitario * quantidade
  const tipo =
    item.tipo === 'plano' || item.tipo === 'avulso' || item.tipo === 'servico'
      ? item.tipo
      : 'servico'

  return {
    ...(item as Partial<PropostaItem>),
    id: typeof item.id === 'string' ? item.id : `item-${index + 1}`,
    descricao,
    tipo,
    quantidade,
    valorUnitario,
    total,
  } as PropostaItem
}

export function parseFinanceProposalSnapshot(
  snapshot: FinanceProposalSnapshotRow,
): ParsedFinanceProposalSnapshot {
  const payload = snapshot.payloadJson ?? {}
  const rawItems = Array.isArray(payload.items) ? payload.items : []
  const items = rawItems
    .map((item, index) => normalizeItem(item, index))
    .filter((item): item is PropostaItem => item != null)

  const meta = parseDados(payload.dados)
  const pdfUrl = snapshot.pdfUrl ?? payload.link ?? null
  const comboVariations =
    meta.comboVariations ??
    (Array.isArray((payload as { comboVariations?: ComboVariation[] }).comboVariations)
      ? (payload as { comboVariations: ComboVariation[] }).comboVariations
      : [])

  return { items, meta, pdfUrl, comboVariations }
}
